import { useEffect, useState } from 'react';
import { useT } from '../../i18n';
import { readBookingPaymentStatus, type BookingPaymentStatus } from '../../lib/api/memberPayment';
import { takePaymentReturn, type PaymentReturn } from '../../lib/paymentReturn';

// ═══════════════════════════════════════════════════════════════════
// «DID IT WORK?» — SAID ONCE, AFTER COMING BACK FROM VIPPS (#508).
//
// The page has just loaded from Stripe's return URL; `paymentReturnBoot` took
// the parameters out of the address and kept them here. This says what
// happened, and the words come from the SERVER wherever it has one:
//
//   paid        the booking's `paymentStatus` is `paid` — the signed webhook has
//               landed. The only state that means money moved (decision 8).
//   gone        the booking no longer exists — the hold lapsed (#607).
//   unavailable the payment route is not there — no provider (#1126). Neutral:
//               the court is still held, there is just nothing to pay through.
//   failed      Stripe told the browser the authentication did not succeed —
//               declined, cancelled, or the five Vipps minutes ran out — and the
//               server has not recorded a payment. The court is still held, and
//               «Betal nå» is still on the booking.
//   confirming  Stripe said `succeeded` or `processing` and the webhook has not
//               landed yet. Asked again, bounded, exactly as `PayForBooking` asks
//               after a card (#1105); past the bound it says so and stops.
//
// `redirect_status` is what Stripe told the BROWSER. It decides only which of
// the not-yet-paid sentences to show. A redirect URL is a URL and can be
// visited, so it never makes anything «paid» (PAYMENTS.md § what a browser can
// prove).
// ═══════════════════════════════════════════════════════════════════

export type ReturnVerdict =
  'checking' | 'paid' | 'gone' | 'unavailable' | 'failed' | 'confirming' | 'slow';

/** Stripe's `redirect_status` values that mean «the payer approved it». */
const HOPEFUL = new Set(['succeeded', 'processing']);

/**
 * Pure: what to say, from what Stripe told the browser and what the server says.
 *
 * The server's two decisive answers win outright. Without one, a `failed`
 * redirect is a failure and anything hopeful is «being confirmed» — including a
 * server we could not reach, which has learned nothing and is not a verdict.
 */
export function returnVerdict(
  redirectStatus: string,
  server: BookingPaymentStatus,
): Exclude<ReturnVerdict, 'checking' | 'slow'> {
  if (server.state === 'ok' && server.paymentStatus === 'paid') return 'paid';
  if (server.state === 'gone') return 'gone';
  // The route is not there (#1126): no provider on this deployment. Not a
  // verdict about the payment — and never «gone», the court is still held.
  if (server.state === 'not_configured') return 'unavailable';
  return HOPEFUL.has(redirectStatus) ? 'confirming' : 'failed';
}

const POLL_INTERVAL_MS = 2_000;
const POLL_ATTEMPTS = 15;

export function PaymentReturnNotice({
  /** Injectable for tests; the default takes the one captured on boot. */
  initial,
}: {
  initial?: PaymentReturn | null;
}) {
  const t = useT();
  // TAKEN, NOT PEEKED: one announcement per return. The address was already
  // cleaned on boot, so a reload has nothing to announce either.
  const [ret] = useState<PaymentReturn | null>(() =>
    initial === undefined ? takePaymentReturn() : initial,
  );
  const [verdict, setVerdict] = useState<ReturnVerdict>('checking');
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (ret === null) return undefined;
    let cancelled = false;
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const ask = async () => {
      attempts += 1;
      const server = await readBookingPaymentStatus(ret.bookingId, ret.subject);
      if (cancelled) return;
      const now = returnVerdict(ret.redirectStatus, server);
      if (now !== 'confirming') {
        setVerdict(now);
        return;
      }
      if (attempts >= POLL_ATTEMPTS) {
        setVerdict('slow');
        return;
      }
      setVerdict('confirming');
      timer = setTimeout(() => void ask(), POLL_INTERVAL_MS);
    };
    void ask();
    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [ret]);

  if (ret === null || dismissed) return null;

  const message =
    verdict === 'checking'
      ? t('booking.pay.return.checking')
      : verdict === 'paid'
        ? t('booking.pay.return.paid')
        : verdict === 'gone'
          ? t('booking.pay.holdLost')
          : verdict === 'unavailable'
            ? t('booking.pay.notConfigured')
            : verdict === 'failed'
              ? t('booking.pay.return.failed')
              : verdict === 'slow'
                ? t('booking.pay.stillConfirming')
                : t('booking.pay.return.confirming');
  const bad = verdict === 'failed' || verdict === 'gone';

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-0 top-0 z-50 flex justify-center px-4 pt-4 pointer-events-none"
      data-testid="payment-return-notice"
      data-verdict={verdict}
    >
      <div
        className={`pointer-events-auto w-full max-w-md rounded-2xl px-4 py-3 shadow-lg flex items-start gap-3 text-[13px] ${
          bad ? 'bg-rose-50 text-rose-800' : 'bg-white text-[#0f1611]'
        }`}
      >
        <p className="flex-1" data-testid="payment-return-message">
          {message}
        </p>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="shrink-0 rounded-xl px-3 py-1 text-[12px] font-semibold border border-current"
          data-testid="payment-return-dismiss"
        >
          {t('booking.pay.return.dismiss')}
        </button>
      </div>
    </div>
  );
}
